/**
 * Counters (D244) — an amount the world holds, declared in data: the air in
 * a suit, the spares clipped to its back, the canisters a rack has given,
 * the cycles a hatch has answered.
 *
 * @remarks
 * Four pieces, one idea:
 *
 * - **The declaration** ({@link counterDefinitionSchema}) — an integer with
 *   a range, a starting value, whose fact it is at the checkpoint (D146's
 *   two words, as flags and durability wear them), and what happens the
 *   moment it reaches its floor or its ceiling (`onMin`, `onMax`).
 * - **The two effects** (`adjust_counter`, `set_counter` in `effects.ts`),
 *   clamped to the range. Clamping is the counter's promise, not the
 *   pack's: no sequence of effects can put thirteen minutes in a twelve
 *   minute canister.
 * - **The one condition** (`counter_at_least` in `counter-conditions.ts`).
 *   "At most", "exactly" and "below" are spellings of it under `not` and
 *   `all_of` — one reading, one spelling, the rule D229 kept for
 *   `at_least_of`.
 * - **The number in the text** — `{counter:<counterId>}` anywhere a pack
 *   writes prose, replaced with the counter's value at the moment the line
 *   is spoken ({@link COUNTER_TOKEN_PATTERN}).
 *
 * And one thing built from them: a **stock** ({@link itemStockSchema}), a
 * fixed thing the player takes from while a counter allows — the rack of
 * canisters, the emergency locker's one spare — so `take a canister from
 * the rack` is the verb the player already knows, and the pack's refusal
 * slot never has to speak a success (content-standards §2.6).
 *
 * The world holds a counter as a DELTA (`world.counterValues`): a counter
 * absent from the record is at its declared `initial`, the "no derived
 * data" rule D152's `spentUses` keeps. So a pack that grows a counter in a
 * later version needs nothing from the saves already in the world.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { counterIdSchema } from './identifiers.js';
import { factScopeSchema, nonBlankStringSchema, positiveIntegerSchema } from './primitives.js';
import { narrativeLineSchema } from './transcript.js';

/**
 * One declared counter.
 *
 * @remarks
 * `min` and `max` bound every value the counter can take, `initial` among
 * them; a range of one value is a constant and is refused (a counter that
 * cannot move is a number written in the wrong place).
 *
 * `onMin` fires when an effect moves the counter ONTO its floor from above
 * — the tick that spends the suit's last minute selects the death in the
 * same pass, never a turn later (D152's `onSpent`, for an amount). `onMax`
 * is its twin at the ceiling. A move that leaves the counter where it was
 * (already at the floor, and told to go lower) is no crossing and fires
 * nothing; a counter that STARTS at its floor has not crossed it.
 *
 * `scope` is whose fact the amount is at the checkpoint: `world` goes back
 * with the photograph (the canister in the rack is there again), `player`
 * survives it the way the hands do. Default `world`, as for flags.
 */
export const counterDefinitionSchema = z
	.strictObject({
		counterId: counterIdSchema,
		/** An authoring note — never shown to the player. */
		description: nonBlankStringSchema.optional(),
		initial: z.int(),
		max: z.int(),
		min: z.int(),
		/** Effects applied the moment an effect moves the counter onto `max` from below. */
		onMax: z.array(effectSchema).readonly().default([]),
		/** Effects applied the moment an effect moves the counter onto `min` from above. */
		onMin: z.array(effectSchema).readonly().default([]),
		scope: factScopeSchema.default('world'),
	})
	.readonly()
	.refine((counter) => counter.min < counter.max, {
		message: 'a counter needs room to move — min must be below max (a counter that cannot move is a constant)',
		path: ['max'],
	})
	.refine((counter) => counter.initial >= counter.min && counter.initial <= counter.max, {
		message: 'a counter starts inside its own range — initial must lie between min and max',
		path: ['initial'],
	});

/** One declared counter — see {@link counterDefinitionSchema}. */
export type CounterDefinition = z.infer<typeof counterDefinitionSchema>;

/** One amount a stock's take gives: this counter, by this many. */
export const stockGiftSchema = z
	.strictObject({
		by: positiveIntegerSchema,
		counterId: counterIdSchema,
	})
	.readonly();

/** One amount a take gives — see {@link stockGiftSchema}. */
export type StockGift = z.infer<typeof stockGiftSchema>;

/**
 * One way a stock can be drawn from — the first whose `when` holds answers
 * the take.
 *
 * @remarks
 * `drawnCounterId` counts what this stock has given: each take moves it one
 * step toward its `max`, and at its `max` the stock is EMPTY — the rack's
 * six clips, the locker's one. `gives` is what a take adds; when any of
 * those counters would pass its own `max`, the stock refuses FULL and
 * nothing moves (the suit has four clips, and a fifth canister is not
 * clipped and thrown away). `takeNarratives` speak the draw (absent, the
 * `takeAcknowledgement` slot); `emptyNarratives` and `fullNarratives` the
 * two refusals (absent, the `stockEmpty` and `stockFull` slots); `effects`
 * apply after the draw has been counted, so they read the new amounts.
 *
 * Several draws exist because one stock can be paid for in different ways
 * by road: Bede's rack, drawn against his book on one road and taken from
 * on another, is two draws counting two different counters.
 */
export const stockDrawSchema = z
	.strictObject({
		drawnCounterId: counterIdSchema,
		effects: z.array(effectSchema).readonly().default([]),
		emptyNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		fullNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		gives: z.array(stockGiftSchema).min(1).readonly(),
		takeNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		when: conditionSchema.optional(),
	})
	.readonly();

/** One way a stock is drawn from — see {@link stockDrawSchema}. */
export type StockDraw = z.infer<typeof stockDrawSchema>;

/**
 * D244 (Act II §13.1d) — a fixed thing the player takes FROM: `take` on it
 * draws instead of lifting it, while its counter allows.
 *
 * @remarks
 * The thing never enters the hands (the schema refuses a stock on a
 * portable thing — it would be two answers to one verb). A thing's own
 * `takeRefusal` still speaks first, as it does for every take (D199): it
 * is how a pack says "not now" in the thing's words — Bede's hand on the
 * rack before yours — and when no draw's `when` holds and no refusal
 * applies, the take refuses as any fixed thing does (`notPortable`).
 */
export const itemStockSchema = z
	.strictObject({
		draws: z.array(stockDrawSchema).min(1).readonly(),
	})
	.readonly();

/** A thing's stock — see {@link itemStockSchema}. */
export type ItemStock = z.infer<typeof itemStockSchema>;

/**
 * D244 (Act II §13.1c) — where a counter's value is printed in prose:
 * `{counter:counter_suit_o2}`, replaced at the moment the line is spoken.
 *
 * @remarks
 * The one spelling, matched by one pattern shared by the three readers —
 * the engine that fills it, the loader that refuses an undeclared name, and
 * the prose audit that reads the line without it. The name inside is
 * captured loosely (anything up to the brace) so a malformed one is SEEN
 * and refused by the loader, rather than passed through as literal text.
 */
export const COUNTER_TOKEN_PATTERN = /\{counter:([^}]*)\}/gu;

/**
 * Every counter id a piece of prose prints, in order (repeats kept).
 *
 * @param text - Authored prose
 * @returns The names inside its `{counter:…}` tokens, as written
 */
export const counterIdsPrintedIn = (text: string): readonly string[] => [...text.matchAll(COUNTER_TOKEN_PATTERN)].map((match) => String(match[1]));

/**
 * Replace every `{counter:…}` token in a piece of prose.
 *
 * @param text - Authored prose
 * @param valueOf - What each named counter prints as
 * @returns The prose with every token replaced
 */
export const withCounterTokensReplaced = (text: string, valueOf: (counterId: string) => string): string =>
	text.replaceAll(COUNTER_TOKEN_PATTERN, (_token, counterId: string) => valueOf(counterId));
